import type { MaterialsFileRef, StudyFileMeta } from '@sigora/domain';
import { type DragEvent, useRef, useState } from 'react';

import { materialsFileUrl } from '../api/client.ts';
import { MATERIALS_FILE_ACCEPT, fileSize, fileSummary, typeFoundNote } from './materials-files.ts';

/** Where in the intake a file goes: the text intake's list, or one paired version. */
export type FileSlot = 'text' | 0 | 1;

/** A file on its way to the study, or one that did not get there. */
export interface Upload {
  key: string;
  slot: FileSlot;
  file: File;
  sent: number;
  total: number;
  status: 'uploading' | 'failed';
  /** The refusal as the server worded it, or why the file was never sent. */
  error: string | null;
  /** Whether sending the same file again could work: not for a file the refusal was about. */
  retryable: boolean;
}

/** Whether participants may see a saved image, and how to change it. */
export interface StimulusControl {
  /** The images the saved version names, which are the only ones a choice can be made about. */
  saved: ReadonlySet<string>;
  chosen: ReadonlySet<string>;
  disabled: boolean;
  onChange: (fileId: string, candidate: boolean) => void;
}

export interface MaterialsFilesProps {
  studyId: string;
  slot: FileSlot;
  /** The id of the file input, which the label names. */
  inputId: string;
  label: string;
  multiple: boolean;
  refs: readonly MaterialsFileRef[];
  known: ReadonlyMap<string, StudyFileMeta>;
  uploads: readonly Upload[];
  /** Said once over the list, such as a file that was already there. */
  notice: string | null;
  readOnly: boolean;
  stimulus: StimulusControl;
  onPick: (files: readonly File[]) => void;
  onRemove: (fileId: string) => void;
  onRetry: (key: string) => void;
  onDismiss: (key: string) => void;
}

const slotId = (slot: FileSlot): string => String(slot);

/**
 * The files one intake holds, and the way to add more: a drop zone around a file input, which a
 * "Choose a file" button opens so the whole control works from the keyboard. Each file shows what
 * Sigora found it to be — its stored name, type, size and pixel size — rather than what was picked,
 * and each upload shows its progress, then its refusal in the server's words with a retry where
 * one could help. Removing a file takes it out of what the next save names and nothing else.
 */
export const MaterialsFiles = ({
  studyId,
  slot,
  inputId,
  label,
  multiple,
  refs,
  known,
  uploads,
  notice,
  readOnly,
  stimulus,
  onPick,
  onRemove,
  onRetry,
  onDismiss,
}: MaterialsFilesProps) => {
  const input = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const id = slotId(slot);

  const dragOver = (event: DragEvent) => {
    if (readOnly) {
      return;
    }

    event.preventDefault();
    setDragging(true);
  };

  const drop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!readOnly) {
      onPick(Array.from(event.dataTransfer.files));
    }
  };

  return (
    <div className="materials-files" data-test-id={`materials-files-${id}`}>
      {!readOnly && (
        <div
          className={dragging ? 'materials-drop materials-drop-active' : 'materials-drop'}
          data-test-id={`materials-drop-${id}`}
          onDragOver={dragOver}
          onDragLeave={() => setDragging(false)}
          onDrop={drop}
        >
          <label htmlFor={inputId}>{label}</label>
          <input
            id={inputId}
            ref={input}
            className="visually-hidden"
            data-test-id={`materials-file-input-${id}`}
            type="file"
            tabIndex={-1}
            multiple={multiple}
            accept={MATERIALS_FILE_ACCEPT}
            onChange={(event) => {
              onPick(Array.from(event.target.files ?? []));
              // Cleared so the same file can be picked again after a refusal or a removal.
              event.target.value = '';
            }}
          />
          <p className="materials-drop-hint">
            {multiple ? 'Drop files here, or ' : 'Drop a file here, or '}
            <button
              type="button"
              data-test-id={`materials-file-choose-${id}`}
              onClick={() => input.current?.click()}
            >
              {!multiple && refs.length > 0 ? 'Choose another file' : 'Choose a file'}
            </button>
          </p>
        </div>
      )}

      {notice !== null && (
        <p className="materials-hint" data-test-id={`materials-files-notice-${id}`} role="status">
          {notice}
        </p>
      )}

      {(refs.length > 0 || uploads.length > 0) && (
        <ul className="materials-file-list">
          {refs.map((ref) => {
            const meta = known.get(ref.file_id);
            const note = meta === undefined ? null : typeFoundNote(meta);
            const offered = ref.kind === 'image' && stimulus.saved.has(ref.file_id);

            return (
              <li
                className="materials-file"
                key={ref.file_id}
                data-test-id={`materials-file-${ref.file_id}`}
              >
                {ref.kind === 'image' && (
                  <img
                    className="materials-file-thumb"
                    data-test-id="materials-file-thumb"
                    src={materialsFileUrl(studyId, ref.file_id)}
                    alt=""
                  />
                )}
                <div className="materials-file-body">
                  <span className="materials-file-name" data-test-id="materials-file-name">
                    {meta?.name ?? 'A saved file'}
                  </span>
                  {meta !== undefined && (
                    <span className="materials-file-summary" data-test-id="materials-file-summary">
                      {fileSummary(meta)}
                    </span>
                  )}
                  {note !== null && (
                    <span className="materials-file-note" data-test-id="materials-file-note">
                      {note}
                    </span>
                  )}
                  {offered && (
                    <label className="materials-file-stimulus">
                      <input
                        type="checkbox"
                        data-test-id="materials-file-stimulus"
                        checked={stimulus.chosen.has(ref.file_id)}
                        disabled={readOnly || stimulus.disabled}
                        onChange={(event) => stimulus.onChange(ref.file_id, event.target.checked)}
                      />
                      Participants may see this image
                    </label>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      className="materials-file-remove"
                      data-test-id="materials-file-remove"
                      aria-label={`Remove ${meta?.name ?? 'this file'}`}
                      onClick={() => onRemove(ref.file_id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            );
          })}

          {uploads.map((upload) => (
            <li className="materials-file" key={upload.key} data-test-id="materials-upload">
              <div className="materials-file-body">
                <span className="materials-file-name" data-test-id="materials-upload-name">
                  {upload.file.name}
                </span>
                {upload.status === 'uploading' ? (
                  <>
                    <progress
                      className="materials-file-progress"
                      data-test-id="materials-upload-progress"
                      aria-label={`Uploading ${upload.file.name}`}
                      max={upload.total}
                      value={upload.sent}
                    />
                    <span className="materials-file-summary">
                      {`Uploading: ${fileSize(upload.sent)} of ${fileSize(upload.total)}`}
                    </span>
                    <button
                      type="button"
                      data-test-id="materials-upload-stop"
                      aria-label={`Stop uploading ${upload.file.name}`}
                      onClick={() => onDismiss(upload.key)}
                    >
                      Stop
                    </button>
                  </>
                ) : (
                  <>
                    <p
                      className="chat-error materials-file-error"
                      data-test-id="materials-upload-error"
                      role="alert"
                    >
                      {upload.error}
                    </p>
                    <div className="materials-row-actions">
                      {upload.retryable && (
                        <button
                          type="button"
                          data-test-id="materials-upload-retry"
                          aria-label={`Try ${upload.file.name} again`}
                          onClick={() => onRetry(upload.key)}
                        >
                          Try again
                        </button>
                      )}
                      <button
                        type="button"
                        data-test-id="materials-upload-dismiss"
                        aria-label={`Dismiss ${upload.file.name}`}
                        onClick={() => onDismiss(upload.key)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
